import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api } from "../lib/api";
import { PageHeader } from "../components/common";
import { LandingStar } from "../components/WeeklyStar";
import { RoleMatrix } from "./admin/Roles";
import { PRINCIPLES, STAGES, SHEETS, SALES_MAPPING, ORDER_MAPPING, ENTITIES, RELATIONS, RISKS, RESIDUAL } from "../data/blueprint";

const ST = { Delivered: "text-emerald-400 border-emerald-500/40", "Sales done": "text-emerald-400 border-emerald-500/40", "In review": "text-amber-400 border-amber-500/40", Partial: "text-sky-400 border-sky-500/40", Next: "text-primary border-primary/50", Planned: "text-muted-foreground" };
const SEV = { High: "text-rose-400", Medium: "text-amber-400", Low: "text-sky-400" };

const Overview = () => (
  <div className="grid lg:grid-cols-12 gap-4">
    <div className="lg:col-span-5 space-y-2">
      {PRINCIPLES.map(([t, d], i) => (
        <div key={t} className="ax-panel p-4 ax-fade" style={{ animationDelay: `${i * 60}ms` }}>
          <div className="flex gap-3"><span className="num text-primary text-sm">0{i + 1}</span><div><div className="font-medium">{t}</div><div className="text-sm text-muted-foreground">{d}</div></div></div>
        </div>
      ))}
    </div>
    <div className="lg:col-span-7 ax-panel p-4" data-testid="blueprint-plan">
      <div className="ax-label mb-3">Phased implementation plan</div>
      {STAGES.map(([s, t, d, st]) => (
        <div key={s} className="flex gap-4 py-3 border-b last:border-0">
          <div className="num text-xs text-muted-foreground w-16 shrink-0">{s}</div>
          <div className="flex-1"><div className="font-medium text-sm">{t}</div><div className="text-xs text-muted-foreground">{d}</div></div>
          <span className={`text-[11px] border px-2 h-6 flex items-center shrink-0 ${ST[st]}`}>{st}</span>
        </div>
      ))}
    </div>
  </div>
);

const Inventory = () => (
  <div className="space-y-3" data-testid="blueprint-inventory">
    {SHEETS.map((s) => (
      <div key={s.sheet} className="ax-panel p-4">
        <div className="flex flex-wrap justify-between gap-2">
          <div><div className="ax-label">{s.file}</div><div className="font-medium">{s.sheet}</div></div>
          <div className="flex gap-2 text-xs"><span className="border px-2 py-1">{s.kind}</span>{s.rows > 0 && <span className="border px-2 py-1 num">{s.rows.toLocaleString("en-IN")} rows</span>}<span className="border border-primary/40 text-primary px-2 py-1">→ {s.target}</span></div>
        </div>
        <ul className="mt-3 grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-muted-foreground list-disc pl-5">{s.findings.map((f) => <li key={f}>{f}</li>)}</ul>
      </div>
    ))}
  </div>
);

const MapTable = ({ rows, head }) => (
  <div className="ax-panel overflow-x-auto mb-6"><table className="ax-table w-full min-w-[800px]">
    <thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
    <tbody>{rows.map((r) => <tr key={r[0]}>{r.map((c, i) => <td key={head[i]} className={i === 1 ? "font-mono text-xs text-primary" : ""}>{c}</td>)}</tr>)}</tbody></table></div>
);

const DataModel = () => (
  <div className="grid lg:grid-cols-12 gap-4" data-testid="blueprint-erd">
    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-2">
      {ENTITIES.map(([e, d]) => <div key={e} className="ax-panel p-3"><div className="font-mono text-sm text-primary">{e}</div><div className="text-xs text-muted-foreground mt-1">{d}</div></div>)}
    </div>
    <div className="lg:col-span-5 ax-panel p-4">
      <div className="ax-label mb-3">Relationships</div>
      {RELATIONS.map((r) => <div key={r} className="font-mono text-xs py-1.5 border-b border-border/50">{r}</div>)}
      <div className="ax-label mt-5 mb-2">Integrity rules</div>
      <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
        <li>Unique business IDs (LD-######, AC-#####, and UIDs as given), with internal IDs kept separate</li>
        <li>Each record has a version number. If two people edit at once, the second save is rejected and they're asked to reload</li>
        <li>Imports match rows by a source key, so re-running never duplicates. Leads edited in the app are never overwritten</li>
        <li>Indexes on owner, branch, status, phone, email, domain and dates to keep lists fast as data grows</li>
      </ul>
    </div>
  </div>
);

const Permissions = () => {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState(null);
  useEffect(() => { api.get("/admin/roles").then((r) => setRoles(r.data)).catch(() => {}); api.get("/admin/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="blueprint-permissions">
      <div className="grid md:grid-cols-4 gap-2 mb-4 text-sm">
        {[["Module", "Sales, Orders, Operations, BP, Dashboard, Admin"], ["Branch", "Mumbai, Delhi (FBD), All (*), future branches"], ["Record scope", "Own · Team · Branch · All"], ["Field", "Edit · View · Mask · None (none is the default for sensitive fields)"]].map(([t, d]) => (
          <div key={t} className="ax-panel p-3"><div className="ax-label">{t}</div><div className="mt-1">{d}</div></div>))}
      </div>
      <p className="text-sm text-muted-foreground mb-3">Live role matrix: these are the actual rules the server enforces. Actions: create, read, update, delete, approve, assign, import, copy, export, print, share.</p>
      {catalog && <RoleMatrix roles={roles} catalog={catalog} />}
    </div>
  );
};

const Risks = () => (
  <div className="grid lg:grid-cols-2 gap-4" data-testid="blueprint-risks">
    <div className="ax-panel p-4"><div className="ax-label mb-3">Migration risks & open questions</div>
      {RISKS.map(([s, r, m]) => <div key={r} className="py-2 border-b border-border/50"><div className="text-sm"><span className={`num text-xs mr-2 ${SEV[s]}`}>{s}</span>{r}</div><div className="text-xs text-muted-foreground mt-0.5">{m}</div></div>)}
    </div>
    <div className="ax-panel p-4"><div className="ax-label mb-3">Residual security limitations (stated honestly)</div>
      <ul className="text-sm text-muted-foreground list-disc pl-4 space-y-2">{RESIDUAL.map((r) => <li key={r}>{r}</li>)}</ul>
      <div className="ax-label mt-5 mb-2">Migration phases</div>
      <div className="text-sm space-y-1">
        <div><span className="text-emerald-400">A · Discovery</span>: done (this blueprint)</div>
        <div><span className="text-emerald-400">B · Data preparation</span>: Sales Tracker cleaned & standardised on import</div>
        <div><span className="text-emerald-400">C · Controlled import</span>: Sales Tracker + Suspects imported, reconciled, reversible</div>
        <div><span className="text-amber-400">D · Parallel verification</span>: you compare against the Sheets</div>
        <div className="text-muted-foreground">E · Cutover: after Orders, Operations and BP are migrated</div>
      </div>
    </div>
  </div>
);

export default function Blueprint() {
  return (
    <div>
      <PageHeader eyebrow="Stage 1 · Discovery & blueprint" title="Axiom One blueprint" desc="Based on your 5 workbooks: Sales Tracker, Order Tracker and Business Plan FY 23-24, 24-25 and 25-26. Where a business meaning wasn't certain, we asked you or listed it as an open question rather than guessing." />
      <LandingStar />
      <Tabs defaultValue="overview">
        <TabsList className="mb-4 flex-wrap h-auto bg-card border rounded-none">
          {[["overview", "Principles & plan"], ["inventory", "Sheet inventory"], ["mapping", "Field mapping"], ["model", "Data model"], ["perms", "Permissions"], ["risks", "Risks"]].map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`blueprint-tab-${k}`} className="rounded-none">{l}</TabsTrigger>))}
        </TabsList>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="inventory"><Inventory /></TabsContent>
        <TabsContent value="mapping">
          <div className="ax-label mb-2">Sales Tracker → Axiom One (migrated)</div>
          <MapTable rows={SALES_MAPPING} head={["Sheet column", "Target", "Class", "Rule"]} />
          <div className="ax-label mb-2">Order Tracker → Axiom One (planned, Stage 4b)</div>
          <MapTable rows={ORDER_MAPPING} head={["Sheet column", "Target", "Rule"]} />
        </TabsContent>
        <TabsContent value="model"><DataModel /></TabsContent>
        <TabsContent value="perms"><Permissions /></TabsContent>
        <TabsContent value="risks"><Risks /></TabsContent>
      </Tabs>
    </div>
  );
}
